/**
 * Analytics API.
 *
 * Each function has a stable signature and returns a Promise — swap the
 * mock branch for real `apiFetch` calls when the backend endpoints exist.
 *
 * The shape of each response is the contract with the UI. If the backend
 * returns something different, a transform layer lives in this file (not
 * the components).
 */

import {
  ANALYTICS_SUMMARY,
  AUTOMATION_RATE_SERIES,
  BEFORE_AFTER_FILES,
  COVERHOLDERS,
  FIELD_COMPLETENESS,
  HUMAN_ATTENTION,
  KB_GROWTH_SERIES,
  VESSELS,
  WORK_DONE_COUNTERS,
  buildDeductibleStructure,
  buildNavZoneExposure,
  isWithinDateRange,
} from "@/mocks/analyticsData";
import type {
  AnalyticsFilterState,
  AnalyticsSummary,
  AutomationRatePoint,
  ClassSocietyDatum,
  CoverholderOption,
  DeductibleRow,
  DqBucketDatum,
  FieldCompletenessDatum,
  FileBeforeAfter,
  FlagStateDatum,
  HumanAttentionSegment,
  KbGrowthPoint,
  NavZoneExposureRow,
  ReviewQueueRow,
  SiByTypeDatum,
  Vessel,
  WorkDoneCounter,
  YearBuiltBucket,
} from "@/types/analytics";

// ── Timing helpers ──────────────────────────────────────────

// import { isUsingMocks } from "@/config/app";
// const shouldUseMocks = isUsingMocks;
// \/ LIES. ALL LIES. (UNTIL THE API SHIPS. /\)
const shouldUseMocks = () => true;

function mockDelay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// ── Filter application (shared by every vessel-derived fetch) ──

function applyFilters(vessels: Vessel[], f: AnalyticsFilterState): Vessel[] {
  return vessels.filter((v) => {
    if (f.coverholder !== "all" && v.coverholder !== f.coverholder) return false;
    if (f.vesselType !== "all" && v.typeKey !== f.vesselType) return false;
    if (f.dqStatus === "pass" && v.dqStatus !== "pass") return false;
    if (f.dqStatus === "fail" && v.dqStatus !== "fail") return false;
    if (!isWithinDateRange(v.addedAt, f.dateFrom, f.dateTo)) return false;
    return true;
  });
}

// ── Reference lookups ───────────────────────────────────────

export async function fetchCoverholderOptions(): Promise<CoverholderOption[]> {
  if (shouldUseMocks()) return mockDelay(COVERHOLDERS, 60);
  throw new Error("analyticsApi.fetchCoverholderOptions: live endpoint not implemented");
}

// ── Header summary ──────────────────────────────────────────

export async function fetchAnalyticsSummary(
  filters: AnalyticsFilterState
): Promise<AnalyticsSummary> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    const summary: AnalyticsSummary = {
      ...ANALYTICS_SUMMARY,
      totalVessels: scoped.length,
      totalSumsInsuredUsd: scoped.reduce((a, v) => a + v.siUsd, 0),
      totalPremiumUsd: scoped.reduce((a, v) => a + v.premiumUsd, 0),
      dqPassRate: scoped.length
        ? scoped.filter((v) => v.dqStatus === "pass").length / scoped.length
        : 0,
      agedVesselCount: scoped.filter((v) => 2024 - v.yearBuilt > 20).length,
      agedVesselPercent: scoped.length
        ? scoped.filter((v) => 2024 - v.yearBuilt > 20).length / scoped.length
        : 0,
    };
    return mockDelay(summary);
  }
  throw new Error("analyticsApi.fetchAnalyticsSummary: live endpoint not implemented");
}

// ── Sums insured by type ────────────────────────────────────

export async function fetchSumsInsuredByType(
  filters: AnalyticsFilterState
): Promise<SiByTypeDatum[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    const labels: Record<SiByTypeDatum["typeKey"], string> = {
      tanker: "Tanker",
      container: "Container",
      bulk: "Bulk",
      gas: "Gas",
      general: "Gen Cargo",
      other: "Other",
    };
    const keys: Array<SiByTypeDatum["typeKey"]> = [
      "tanker",
      "container",
      "bulk",
      "gas",
      "general",
      "other",
    ];
    const data = keys.map<SiByTypeDatum>((typeKey) => {
      const group = scoped.filter((v) => v.typeKey === typeKey);
      return {
        typeKey,
        typeLabel: labels[typeKey],
        hmFullUsd: group.filter((v) => v.coverBasis === "hm_full").reduce((a, v) => a + v.siUsd, 0),
        tloUsd: group.filter((v) => v.coverBasis === "tlo").reduce((a, v) => a + v.siUsd, 0),
      };
    });
    return mockDelay(data);
  }
  throw new Error("analyticsApi.fetchSumsInsuredByType: live endpoint not implemented");
}

// ── Fleet by flag state ─────────────────────────────────────

export async function fetchFleetByFlag(filters: AnalyticsFilterState): Promise<FlagStateDatum[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    const byFlag = new Map<string, FlagStateDatum>();
    for (const v of scoped) {
      const existing = byFlag.get(v.flagIsoNum);
      if (existing) existing.vesselCount++;
      else
        byFlag.set(v.flagIsoNum, {
          isoNum: v.flagIsoNum,
          iso2: v.flagIso2,
          name: v.flagName,
          vesselCount: 1,
        });
    }
    return mockDelay(Array.from(byFlag.values()).sort((a, b) => b.vesselCount - a.vesselCount));
  }
  throw new Error("analyticsApi.fetchFleetByFlag: live endpoint not implemented");
}

// ── DQ failure breakdown ────────────────────────────────────

export async function fetchDqFailureBreakdown(
  filters: AnalyticsFilterState
): Promise<DqBucketDatum[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    const counts: Record<string, number> = {
      "data type": 0,
      mandatory: 0,
      summation: 0,
      conditional: 0,
      duplicates: 0,
      pattern: 0,
      "reference list": 0,
      binary: 0,
      other: 0,
    };
    for (const v of scoped) {
      for (const b of v.dqBuckets) counts[b] = (counts[b] ?? 0) + 1;
    }
    const severity = (bucket: string, count: number): DqBucketDatum["severity"] => {
      if (count === 0) return "neutral";
      if (bucket === "reference list" || bucket === "duplicates") return "danger";
      if (bucket === "data type" || bucket === "mandatory" || bucket === "summation") return "warn";
      return "info";
    };
    const data: DqBucketDatum[] = (Object.keys(counts) as Array<DqBucketDatum["bucket"]>)
      .map((bucket) => ({
        bucket,
        count: counts[bucket] ?? 0,
        severity: severity(bucket, counts[bucket] ?? 0),
      }))
      .sort((a, b) => b.count - a.count);
    return mockDelay(data);
  }
  throw new Error("analyticsApi.fetchDqFailureBreakdown: live endpoint not implemented");
}

// ── Year built distribution ─────────────────────────────────

export async function fetchYearBuiltDistribution(
  filters: AnalyticsFilterState
): Promise<YearBuiltBucket[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    const buckets: Array<{ label: string; min: number; max: number }> = [
      { label: "Pre-1990", min: 0, max: 1989 },
      { label: "1990s", min: 1990, max: 1999 },
      { label: "2000s", min: 2000, max: 2009 },
      { label: "2010s", min: 2010, max: 2019 },
      { label: "2020s", min: 2020, max: 2030 },
    ];
    const data = buckets.map((b) => ({
      label: b.label,
      count: scoped.filter((v) => v.yearBuilt >= b.min && v.yearBuilt <= b.max).length,
    }));
    return mockDelay(data);
  }
  throw new Error("analyticsApi.fetchYearBuiltDistribution: live endpoint not implemented");
}

// ── Class society ───────────────────────────────────────────

export async function fetchClassSocietyDistribution(
  filters: AnalyticsFilterState
): Promise<ClassSocietyDatum[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    if (!scoped.length) return mockDelay([]);
    const counts = new Map<string, number>();
    for (const v of scoped) counts.set(v.classSociety, (counts.get(v.classSociety) ?? 0) + 1);
    const total = scoped.length;
    const data: ClassSocietyDatum[] = Array.from(counts.entries())
      .map(([society, count]) => ({
        society: society as ClassSocietyDatum["society"],
        percent: count / total,
      }))
      .sort((a, b) => b.percent - a.percent);
    return mockDelay(data);
  }
  throw new Error("analyticsApi.fetchClassSocietyDistribution: live endpoint not implemented");
}

// ── War-risk / nav-zone exposure ────────────────────────────

export async function fetchNavZoneExposure(
  filters: AnalyticsFilterState
): Promise<NavZoneExposureRow[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    return mockDelay(buildNavZoneExposure(scoped));
  }
  throw new Error("analyticsApi.fetchNavZoneExposure: live endpoint not implemented");
}

// ── Deductible structure ────────────────────────────────────

export async function fetchDeductibleStructure(
  filters: AnalyticsFilterState
): Promise<DeductibleRow[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters);
    return mockDelay(buildDeductibleStructure(scoped));
  }
  throw new Error("analyticsApi.fetchDeductibleStructure: live endpoint not implemented");
}

// ── Field completeness ──────────────────────────────────────

export async function fetchFieldCompleteness(
  _filters: AnalyticsFilterState
): Promise<FieldCompletenessDatum[]> {
  if (shouldUseMocks()) return mockDelay(FIELD_COMPLETENESS);
  throw new Error("analyticsApi.fetchFieldCompleteness: live endpoint not implemented");
}

// ── Review queue ────────────────────────────────────────────

export async function fetchReviewQueue(filters: AnalyticsFilterState): Promise<ReviewQueueRow[]> {
  if (shouldUseMocks()) {
    const scoped = applyFilters(VESSELS, filters)
      .filter((v) => v.dqStatus === "fail" || v.rowFlags.length > 0)
      .slice(0, 40)
      .map<ReviewQueueRow>((v) => ({
        imo: v.imo,
        name: v.name,
        type: v.type,
        typeKey: v.typeKey,
        flagIso2: v.flagIso2,
        yearBuilt: v.yearBuilt,
        siUsd: v.siUsd,
        classSociety: v.classSociety,
        dqStatus: v.dqStatus,
        dqFailureCount: v.dqFailureCount,
        rowFlags: v.rowFlags,
      }));
    return mockDelay(scoped);
  }
  throw new Error("analyticsApi.fetchReviewQueue: live endpoint not implemented");
}

// ── Work-done counters ──────────────────────────────────────

export async function fetchWorkDoneCounters(): Promise<WorkDoneCounter[]> {
  if (shouldUseMocks()) return mockDelay(WORK_DONE_COUNTERS);
  throw new Error("analyticsApi.fetchWorkDoneCounters: live endpoint not implemented");
}

// ── Automation rate over time ───────────────────────────────

export async function fetchAutomationRateSeries(
  filters: AnalyticsFilterState
): Promise<AutomationRatePoint[]> {
  if (shouldUseMocks()) {
    const scoped = AUTOMATION_RATE_SERIES.filter((p) =>
      isWithinDateRange(p.date, filters.dateFrom, filters.dateTo)
    );
    return mockDelay(scoped);
  }
  throw new Error("analyticsApi.fetchAutomationRateSeries: live endpoint not implemented");
}

// ── KB growth ───────────────────────────────────────────────

export async function fetchKbGrowthSeries(filters: AnalyticsFilterState): Promise<KbGrowthPoint[]> {
  if (shouldUseMocks()) {
    const scoped = KB_GROWTH_SERIES.filter((p) =>
      isWithinDateRange(p.date, filters.dateFrom, filters.dateTo)
    );
    return mockDelay(scoped);
  }
  throw new Error("analyticsApi.fetchKbGrowthSeries: live endpoint not implemented");
}

// ── Human attention ─────────────────────────────────────────

export async function fetchHumanAttention(): Promise<HumanAttentionSegment[]> {
  if (shouldUseMocks()) return mockDelay(HUMAN_ATTENTION);
  throw new Error("analyticsApi.fetchHumanAttention: live endpoint not implemented");
}

// ── Before/after ────────────────────────────────────────────

export async function fetchBeforeAfterFiles(
  filters: AnalyticsFilterState
): Promise<FileBeforeAfter[]> {
  if (shouldUseMocks()) {
    const scoped =
      filters.coverholder === "all"
        ? BEFORE_AFTER_FILES
        : BEFORE_AFTER_FILES.filter((f) => f.coverholder === filters.coverholder);
    return mockDelay(scoped);
  }
  throw new Error("analyticsApi.fetchBeforeAfterFiles: live endpoint not implemented");
}
